import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { formatDistance } from 'date-fns';
import React, { useCallback, useEffect, useState } from 'react';
import styled, { keyframes } from 'styled-components';
import { LFC } from '../../../Legacy';
import { ApiClient } from '../../../api/ApiClient';
import { v2 } from '../../../api/ApiInterfaceV2';
import { ClipDataBuilder, ClipboardService } from '../../../platform/Clipboard';
import { Button, Colors, Input } from '../../../ui';
import { Position } from '../../../ui/Position';
import { TextButton } from '../../../ui/TextButton';
import { useResolve } from '../../Container';
import { Copy, DisableFrame, Flexer, FlexerProps, Title } from '../elements/LayoutElements';

const ExpireMessage = styled.span`
    font-size: .875rem;
    color: ${Colors.textLight};
`;

const flash = keyframes`
    0% {
        inset: 0;
        opacity: .5;
        border-width: .125rem;
        border-radius: .25rem;
    }
    100% {
        inset: -1rem;
        opacity: 0;
        border-width: .5rem;
        border-radius: 1rem;
    }
`;

const FlashFrame = styled(Flexer)`
    position: relative;
`;

const FlashEffect = styled.div`
    position: absolute;
    inset: 0;
    border: solid 4px ${Colors.primary};
    border-radius: .25rem;
    opacity: .5;
    animation: ${flash} .15s ease-out;
    animation-fill-mode: forwards;
    pointer-events: none;
`;

const Flash: LFC<{ ticker: number } & FlexerProps> = ({ ticker, children, ...rest }) => {
    return <FlashFrame key={ticker} {...rest}>
        {children}
        {!!ticker && <FlashEffect />}
    </FlashFrame>;
};

const useMemberInviteLinkRefresher = (target: string) => {
    const api = useResolve(ApiClient);
    const qc = useQueryClient();

    return useMutation({
        mutationFn: async () => {
            const type = 'workspace';
            const existing = await api.exec(v2.admin.invites.list(type, target));
            for (const e of existing) {
                await api.exec(v2.admin.invites.delete({ inviteId: e?.id }));
            }
            await api.exec(v2.admin.invites.create({ expiry: '14d', type, target }));
            await qc.invalidateQueries(['admin.invites.list', type, target]);
        },
    });
};

const useMemberInviteLinkQuery = (target: string) => {
    const api = useResolve(ApiClient);

    // Endpoint supports multiple (for legacy reasons) but UI only supports one, so bare that in mind!
    return useQuery({
        queryKey: ['admin.invites.list', 'workspace', target],
        queryFn: async () => {
            const type = 'workspace';
            const invites = await api.exec(v2.admin.invites.list(type, target));
            return invites?.[0] || null;
        },
    });
};

export const MemberInviteViaLinkView = ({ workspaceId }: { workspaceId: string }) => {

    const clipboard = useResolve(ClipboardService);
    const [copied, setCopied] = useState(0);

    const query = useMemberInviteLinkQuery(workspaceId);
    const refresher = useMemberInviteLinkRefresher(workspaceId);

    useEffect(() => {
        if (!query.isLoading && !query.data) refresher.mutate();
    }, [query.data, query.isLoading]);

    const link = query.data;
    const working = query.isLoading || refresher.isLoading;

    const onCopyLink = useCallback(() => {
        if (!link) return;
        const clip = new ClipDataBuilder();
        clip.setText(link.url);
        clipboard.write(clip.build());
        setCopied(x => x + 1);
    }, [link]);

    return <Flexer relative flex="column" gap=".25">
        {!!copied && <Position inset=".25" at="topRight">
            <Title size="sm" flush color={Colors.positive}>Copied!</Title>
        </Position>}
        <Copy flush bold>
            Share this link with your team:
        </Copy>
        <Flexer flex="row">
            <Flexer relative grow margin="0 .25 0 0" background="white">
                <Flash ticker={copied}>
                    <Input type="readonly" value={(link?.url ?? '')} />
                </Flash>
            </Flexer>
            <Button size="md" variant="primary" disabled={working} onAction={onCopyLink}>
                Copy Link
            </Button>
        </Flexer>
        {link &&
            <ExpireMessage>
                <Flexer flex="row" gap=".25">
                    This link will expire in {formatDistance(new Date(), link.expiresAt)}.
                    <DisableFrame disabled={working}>
                        <TextButton underline onClick={() => refresher.mutate()}>Generate new link</TextButton>
                    </DisableFrame>
                </Flexer>
            </ExpireMessage>
        }
        <Flexer height="1" />
    </Flexer >;
};


